<script lang="ts">
  import { onMount, tick } from 'svelte'
  import { MediaQuery } from 'svelte/reactivity'
  import type { OpsPayload } from '../shared/contract'
  import ClassicBoard from './lib/Board.svelte'
  import SignsBoard from './lib/signs/Board.svelte'
  import { devParam } from './lib/dev'
  import { beyond, cut, overflows, trimLines } from './lib/fit'
  import ClassicDock from './lib/Dock.svelte'
  import SignsDock from './lib/signs/Dock.svelte'
  import ClassicHeader from './lib/Header.svelte'
  import SignsHeader from './lib/signs/Header.svelte'
  import ClassicLamp from './lib/Lamp.svelte'
  import SignsLamp from './lib/signs/Lamp.svelte'
  import ClassicPlate from './lib/Plate.svelte'
  import SignsPlate from './lib/signs/Plate.svelte'
  import { report } from './lib/report.svelte'
  import SnowScene from './lib/signs/SnowScene.svelte'
  import Sign from './lib/Sign.svelte'
  import Groomed from './lib/Groomed.svelte'
  import { Appearance, provideAppearance } from './lib/appearance.svelte'
  import { GRADES, exceptions, filtering, liftsLine, loneArea, matches, mountain } from './lib/tally'
  import { View } from './lib/view.svelte'
  import { clockTime, opening } from './lib/when'

  /**
   * Lifts first, exceptions named, counts in the filter. A phone page that
   * never scrolls at rest; desktop area tabs sit above a board naming every run.
   *
   * Development only: `scenario` feeds a recorded day instead of the network,
   * and `pinned` fixes the clock (both read from ?scenario= and ?now=).
   */
  let {
    scenario = devParam('scenario'),
    pinned = devParam('now'),
  }: { scenario?: string | null; pinned?: string | null } = $props()

  const appearance = new Appearance()
  provideAppearance(appearance)
  const signs = $derived(appearance.value === 'signs')
  const Board = $derived(signs ? SignsBoard : ClassicBoard)
  const Dock = $derived(signs ? SignsDock : ClassicDock)
  const Header = $derived(signs ? SignsHeader : ClassicHeader)
  const Lamp = $derived(signs ? SignsLamp : ClassicLamp)
  const Plate = $derived(signs ? SignsPlate : ClassicPlate)
  $effect(() => appearance.apply())

  const view = new View()
  const desktop = new MediaQuery('(min-width: 1000px) and (min-height: 620px)')
  const desk = $derived(desktop.current)

  let fed = $state.raw<OpsPayload | null>(null)
  const feeding = $derived(import.meta.env.DEV && !!scenario)
  const data = $derived(feeding ? fed : report.data)
  const m = $derived(data ? mountain(data.areas) : null)

  let clock = $state(new Date())
  const pin = $derived(pinned && Number.isFinite(Date.parse(pinned)) ? new Date(pinned) : null)
  const now = $derived(pin ?? clock)

  onMount(() => {
    // The clock turns over with the minute, and keeps "checked … ago" honest.
    let timer: ReturnType<typeof setTimeout>
    const minute = () => {
      clock = new Date()
      timer = setTimeout(minute, 60_000 - (Date.now() % 60_000) + 20)
    }
    timer = setTimeout(minute, 60_000 - (Date.now() % 60_000) + 20)
    let stop: (() => void) | undefined
    if (import.meta.env.DEV && scenario) {
      const name = scenario
      void import('./dev/scenarios').then((dev) => (fed = dev.scenario(name)))
    } else stop = report.start()
    return () => {
      clearTimeout(timer)
      stop?.()
    }
  })

  /** One quiet word on the freshness line when the copy on screen may be behind. */
  const quiet = $derived(
    feeding
      ? undefined
      : !report.online
        ? 'offline'
        : report.failed
          ? 'retrying'
          : report.stale
            ? 'cached'
            : undefined,
  )
  /** A stored copy isn't Live until the edge has answered this session. */
  const live = $derived(feeding || report.confirmed)

  const filter = $derived(view.filter)
  const f = $derived(filtering(filter))
  const openArea = $derived(data?.areas.find((a) => a.name === view.open) ?? null)
  const matched = $derived(m ? m.runs.filter((r) => matches(r, filter)).length : 0)
  const lone = $derived(data && !f && !desk && !openArea ? loneArea(data.areas) : null)
  /** Opening day while it is ahead and nothing turns; the worker sends its date all year. */
  const opens = $derived(
    data && m && data.resort.status === 'closed' && m.turning === 0
      ? opening(data.resort.opening, now)
      : null,
  )

  // An area the report no longer has, or a status nothing can match.
  $effect(() => {
    if (data && view.open && !openArea) view.open = null
  })
  $effect(() => {
    if (!m) return
    if (view.status === 'open' && !m.open) view.status = null
    if (view.status === 'groomed' && !m.groomed) view.status = null
  })
  $effect(() => {
    void [view.open, view.status, view.grades]
    view.sync()
  })

  /*
   * The plates fit without the page scrolling, and no one-line list cuts a
   * name. In order: held lifts share a row (their word giving way to the
   * name); lift names fold to lamps, the area with the most lifts first and
   * only as many areas as it takes (the header then names the shorter
   * list), unless early season's one area is naming its open runs;
   * one-line lists end in "+N" where a name won't fit; a named-run list gives
   * up a line at a time, its tail becoming "+N more", which opens the area;
   * then, on a crowded day, the smallest areas give up their exceptions line,
   * which their tallies still count.
   */
  let main = $state<HTMLElement>()
  /** Areas whose lift names have folded to lamps. */
  let lampAreas = $state<string[]>([])
  let holdRows = $state(true)
  let shown = $state<Record<string, number>>({})
  let excShown = $state<Record<string, number>>({})
  let brief = $state<string[]>([])
  let liftsShown = $state<number>()
  let vw = $state(0)
  let vh = $state(0)
  let fitting = 0

  function reset() {
    lampAreas = []
    holdRows = true
    shown = {}
    excShown = {}
    brief = []
    liftsShown = undefined
  }

  async function fit() {
    const run = ++fitting
    const current = async () => {
      await tick()
      return run === fitting && !!main
    }
    reset()
    if (!(await current())) return
    if (overflows(main!) && data!.areas.some((a) => a.lifts.some((l) => l.status))) {
      holdRows = false
      if (!(await current())) return
    }
    if (overflows(main!) && !lone) {
      const most = data!.areas
        .filter((a) => a.lifts.length > 0)
        .toSorted((a, b) => b.lifts.length - a.lifts.length)
      for (const area of most) {
        lampAreas = [...lampAreas, area.name]
        // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
        if (!(await current())) return
        if (!overflows(main!)) break
      }
    }
    if (!(await oneLine(current))) return
    if (!(await trimHits(current))) return
    // Fewest runs first: a small area's closed runs are the quickest to find with a tap.
    const crowded = data!.areas
      .filter((a) => (!f || desk) && exceptions(a, m!.anyOpen))
      .toSorted((a, b) => a.runs.length - b.runs.length)
    for (const area of crowded) {
      if (!overflows(main!)) break
      brief = [...brief, area.name]
      // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
      if (!(await current())) return
    }
    // The last line given up may have freed room for an earlier one: the largest gets it back first.
    for (const name of brief.slice(0, -1).toReversed()) {
      brief = brief.filter((n) => n !== name)
      // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
      if (!(await current())) return
      if (overflows(main!)) {
        brief = [...brief, name]
        // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
        if (!(await current())) return
        continue
      }
      // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
      if (!(await oneLine(current))) return
    }
  }

  /** Exceptions lines and the header's lifts line: one name at a time until each fits. */
  async function oneLine(current: () => Promise<boolean>): Promise<boolean> {
    for (let guard = 40; guard > 0; guard--) {
      let changed = false
      for (const xs of main!.querySelectorAll<HTMLElement>('[data-exc]')) {
        const name = xs.dataset.exc!
        const n = excShown[name] ?? xs.querySelectorAll('[data-x]').length
        if (cut(xs) && n > 0) {
          excShown = { ...excShown, [name]: n - 1 }
          changed = true
        }
      }
      const lifts = document.querySelector<HTMLElement>('header .liftsx')
      const names = liftsLine(m!.lifts).names?.length ?? 0
      if (lifts?.getClientRects().length && cut(lifts) && (liftsShown ?? names) > 0) {
        liftsShown = (liftsShown ?? names) - 1
        changed = true
      }
      if (!changed) return true
      // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
      if (!(await current())) return false
    }
    return true
  }

  /** A filter's matches, or early season's open runs, trimmed to the room left. */
  async function trimHits(current: () => Promise<boolean>): Promise<boolean> {
    const lists = [...main!.querySelectorAll<HTMLElement>('[data-hits]')].filter(
      (list) => list.children.length,
    )
    if (!lists.length) return true
    const row = (lists[0]!.firstElementChild as HTMLElement).offsetHeight
    const lines = trimLines(main!, lists, row)
    const next: Record<string, number> = {}
    lists.forEach((list, i) => {
      const runs = [...list.querySelectorAll<HTMLElement>('[data-hit]')]
      const kept = runs.filter((item) => !beyond(list, lines[i]!, row, item)).length
      if (kept < runs.length) next[list.dataset.hits!] = kept
    })
    if (!Object.keys(next).length) return true
    shown = next
    if (!(await current())) return false
    // "+N more" must sit on the last line kept: one name at a time, each after the last settles.
    for (const [i, list] of lists.entries()) {
      const name = list.dataset.hits!
      for (let guard = 40; guard > 0; guard--) {
        const more = list.querySelector<HTMLElement>('.more')
        if (!more || !beyond(list, lines[i]!, row, more) || !shown[name]) break
        shown = { ...shown, [name]: shown[name]! - 1 }
        // oxlint-disable-next-line no-await-in-loop -- each step is measured after it renders
        if (!(await current())) return false
      }
    }
    return true
  }

  $effect(() => {
    void [data, desk, openArea, lone, filter.status, ...filter.grades, vw, vh, signs]
    // An opened area on a phone has the one list that scrolls; nothing to fit.
    if (data && (desk || !openArea)) void fit()
    else {
      fitting++
      reset()
    }
  })

  /** Focus that fell to the page when its control went away goes somewhere that makes sense. */
  async function refocus(target: () => HTMLElement | null | undefined, always = false) {
    await tick()
    const lost = !document.activeElement || document.activeElement === document.body
    if (always || lost) target()?.focus()
  }
  const areaButton = (name: string) =>
    main?.querySelector<HTMLElement>(`.ph[data-area="${CSS.escape(name)}"]`)

  function closeArea() {
    const name = view.open
    if (!name) return
    const inside = !!main?.querySelector('.plate.open')?.contains(document.activeElement)
    view.close()
    void refocus(() => areaButton(name), inside)
  }

  function clear() {
    view.clear()
    void refocus(() => document.querySelector<HTMLElement>('.dock .state button'))
  }

  const onkeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && view.open && !document.querySelector('dialog[open]')) closeArea()
  }
</script>

<svelte:window {onkeydown} onpopstate={() => view.popstate()} bind:innerWidth={vw} bind:innerHeight={vh} />

{#if signs}<SnowScene />{/if}
{#if data && m}
  <div class="app" class:signs class:classic={!signs} class:desk class:area-open={!desk && !!openArea}>
    <Header
      {data}
      {m}
      {now}
      {quiet}
      {live}
      {opens}
      {filter}
      {matched}
      openMode={!!openArea && !desk}
      lamps={lampAreas.length > 0}
      {liftsShown}
      onclear={clear}
    />
    {#if signs && !desk && openArea}
      <nav class="area-nav" aria-label="Mountain areas">
        <button onclick={closeArea}><span aria-hidden="true">←</span> All areas</button>
        <select aria-label="Choose mountain area" value={openArea.name} onchange={(event) => view.toggleArea(event.currentTarget.value, true)}>
          {#each data.areas as area}<option value={area.name}>{area.name}</option>{/each}
        </select>
      </nav>
    {/if}
    <main bind:this={main} aria-label="Lifts and runs by area">
      {#each data.areas as area}
        {@const isOpen = openArea?.name === area.name}
        {#if !signs || desk || !openArea || isOpen}
        <Plate
          {area}
          anyOpen={m.anyOpen}
          {filter}
          {desk}
          open={!desk && isOpen}
          folded={!desk && !!openArea && !isOpen}
          selected={desk && isOpen}
          named={lone === area.name}
          withLifts={!lampAreas.includes(area.name)}
          {holdRows}
          shown={shown[area.name]}
          excShown={excShown[area.name]}
          brief={brief.includes(area.name)}
          ontoggle={(name) => (view.open === name ? closeArea() : view.toggleArea(name, !desk))}
        />
        {/if}
      {/each}
    </main>
    {#snippet dock()}
      <Dock
        runs={m.runs}
        {filter}
        {desk}
        onstatus={(s) => view.setStatus(s)}
        ongrade={(g) => view.toggleGrade(g)}
      />
    {/snippet}
    {#if desk}
      <Board
        areas={data.areas}
        {filter}
        anyOpen={m.anyOpen}
        {opens}
        focus={openArea}
        onall={closeArea}
        {dock}
      />
      <footer class="legend" aria-label="Key">
        {#if !signs}
          <span>{#each GRADES as g (g)}<Sign grade={g} />{/each} open</span>
          <span>{#each GRADES as g (g)}<Sign grade={g} off />{/each} closed</span>
          <span><Groomed /> groomed</span>
        {/if}
        <span><Lamp on bare /> lift turning</span>
        <span><Lamp bare /> stopped</span>
        <span class="hint">
          {f
            ? 'Faded runs on the board don’t match the filter.'
            : openArea
              ? 'Click the area again, or All areas, for the whole mountain.'
              : 'Click an area to focus the board on it.'}
        </span>
      </footer>
    {:else}
      {@render dock()}
    {/if}
  </div>
{:else}
  <div class="app">
    <header class="bare">
      <div class="clock">{clockTime(now)}</div>
      <div class="fresh">
        {report.failed || !report.online ? 'No report yet · trying again' : 'Reading the report'}
      </div>
    </header>
  </div>
{/if}

<style>
  .app {
    position: relative;
    z-index: 1;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    padding: calc(var(--sat) + 8px) var(--pad) calc(var(--sab) + 6px);
  }
  main {
    position: relative;
    isolation: isolate;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    margin-top: 14px;
  }
  .signs main::before,
  .signs main::after {
    content: '';
    position: absolute;
    z-index: -1;
    top: -10px;
    bottom: 0;
    width: 48px;
    border: 1px solid #8d795c;
    border-radius: 1px;
    background: var(--timber);
    box-shadow: inset 2px 2px 0 #f3e1bd80, inset -7px 0 0 #67523b40, 3px 2px 5px #15282035;
  }
  .signs main::before { left: -7px; }
  .signs main::after { right: -7px; }
  .area-nav { display: flex; flex: none; gap: 0; min-height: 44px; margin-top: 10px; background: #fff; border: 1px solid #fff; color: #263e4c; }
  .area-nav button { display: flex; align-items: center; gap: 8px; min-width: 102px; min-height: 44px; padding: 0 12px; font-size: 13px; }
  .area-nav button span { font-size: 20px; }
  .area-nav select { flex: 1; min-width: 0; min-height: 44px; border: 0; border-left: 1px solid #d3dee3; padding: 0 12px; border-radius: 0; background: #bc263c; color: #fff; font: 700 15px var(--font); cursor: pointer; }
  .area-nav option { background: #fff; color: #263e4c; }
  .signs.area-open main { margin-top: 0; }
  .bare .clock {
    font-size: 15px;
    line-height: 20px;
  }
  .bare .fresh {
    font-size: 12px;
    line-height: 16px;
    color: var(--ink-2);
  }
  @media (max-width: 999px) and (max-height: 760px) {
    main {
      margin-top: 10px;
    }
  }
  .signs.desk {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto 44px minmax(0, 1fr) auto;
    gap: 10px;
    padding: 18px max(24px, calc((100vw - 1600px) / 2)) 20px;
  }
  .signs.desk main {
    grid-column: 1;
    grid-row: 2;
    flex-direction: row;
    margin-top: 0;
    gap: 5px;
  }
  .signs.desk main::before, .signs.desk main::after { display: none; }
  .legend {
    grid-column: 1;
    grid-row: 4;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 14px;
    padding: 0 2px;
    color: var(--ink-2);
    font-size: 12px;
  }
  .legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .legend .hint {
    margin-left: auto;
    color: var(--ink-3);
  }
  .classic.desk {
    display: grid;
    grid-template-columns: 400px minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto;
    column-gap: 28px;
    row-gap: 0;
    padding: 30px max(36px, calc((100vw - 1400px) / 2)) 32px;
  }
  .classic.desk main {
    grid-column: 1;
    grid-row: 2;
    margin-top: 20px;
    overflow-y: auto;
    overscroll-behavior: contain;
    gap: 10px;
  }
  .classic .legend { grid-row: 3; gap: 8px 18px; padding: 14px 4px 0; }
  .classic .legend .hint { flex-basis: 100%; margin-left: 0; }
</style>
